import { OutgoingPrePurchaseFormDto } from 'api/dtos/events/pre-purchase-questions';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { Model, Survey } from 'survey-react-ui';
import { PageModel } from 'survey-core';
import classNames from 'classnames';
import * as Sentry from '@sentry/nextjs';

import { Theme } from 'theme/survey-js/theme';

interface SurveyResponse {
  response: Record<string, any>;
}

interface SurveyCompleteEvent {
  data: Record<string, any>;
  isEditing: boolean;
}

interface Props {
  form: OutgoingPrePurchaseFormDto;
  response: SurveyResponse | null;
  isRenderedInWebview: boolean;
  handleComplete: (event: SurveyCompleteEvent) => void;
  handleSetCanSubmit: (canSubmit: boolean) => void;
}

function Form(
  {
    form,
    response,
    isRenderedInWebview,
    handleComplete,
    handleSetCanSubmit
  }: Props,
  ref: React.RefObject<{ submit: () => void; reset: () => void }>
) {
  const { t } = useTranslation(['checkout']);
  const valueChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSubmit = useCallback(
    ({ data }: { data: Record<string, any> }) => {
      handleComplete({ data, isEditing: !!response });
    },
    [handleComplete, response]
  );

  const checkIfLastVisiblePage = useCallback((surveyModel: Model): boolean => {
    try {
      const allPages = surveyModel.pages || [];
      const visiblePageIndices: number[] = [];

      allPages.forEach((page: PageModel, index: number) => {
        // SurveyJS sets isVisible property dynamically based on visibleIf conditions
        if (page.isVisible !== false) {
          visiblePageIndices.push(index);
        }
      });

      if (visiblePageIndices.length > 0) {
        const lastVisiblePageIndex =
          visiblePageIndices[visiblePageIndices.length - 1];
        return surveyModel.currentPageNo === lastVisiblePageIndex;
      }

      // Fallback: if no visible pages detected, use original logic
      return surveyModel.currentPageNo === surveyModel.pageCount - 1;
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: 'PrePurchaseQuestionsForm',
          action: 'checkIfLastVisiblePage'
        },
        extra: {
          currentPageNo: surveyModel.currentPageNo,
          pageCount: surveyModel.pageCount,
          pagesLength: surveyModel.pages?.length
        }
      });
      // Fallback to original logic
      return surveyModel.currentPageNo === surveyModel.pageCount - 1;
    }
  }, []);

  const handleCurrentPageChanged = useCallback(
    (sender: Model) => {
      const isLastPage = checkIfLastVisiblePage(sender);
      handleSetCanSubmit(isLastPage);
    },
    [checkIfLastVisiblePage, handleSetCanSubmit]
  );

  useImperativeHandle(ref, () => ({
    submit: () => survey?.tryComplete(),
    reset: () => survey?.clear(false, true)
  }));

  const schema = form?.schema;
  const hasTitle = !!schema?.title;
  const hasDescription = !!schema?.description;

  const showTitleProperty = schema?.showTitle;
  const shouldShowTitleAndDescription =
    (hasTitle || hasDescription) && showTitleProperty !== false;

  const survey = useMemo(() => {
    if (!form) {
      return null;
    }

    const surveyForm = new Model(form.schema);
    surveyForm.applyTheme(Theme);
    // Always keep showTitle false - we render title/description manually at the top
    surveyForm.showTitle = false;
    surveyForm.showPageTitles = false;
    surveyForm.showCompleteButton = false;
    surveyForm.completedHtml = '';
    surveyForm.showCompletedPage = false;
    surveyForm.onComplete.add(handleSubmit);
    surveyForm.onCurrentPageChanged.add(handleCurrentPageChanged);
    // Re-evaluate last page when values change with debouncing
    surveyForm.onValueChanged.add((sender: Model) => {
      // Clear any existing timeout
      if (valueChangeTimeoutRef.current) {
        clearTimeout(valueChangeTimeoutRef.current);
      }

      // Debounce the check to avoid excessive calls
      valueChangeTimeoutRef.current = setTimeout(() => {
        handleCurrentPageChanged(sender);
        valueChangeTimeoutRef.current = null;
      }, 100);
    });
    surveyForm.data = response?.response;

    return surveyForm;
  }, [
    form,
    response,
    handleSubmit,
    handleCurrentPageChanged,
    shouldShowTitleAndDescription,
    hasTitle
  ]);

  // Only re-check when survey instance changes or page navigation occurs
  useEffect(() => {
    if (survey) {
      const isLastPage = checkIfLastVisiblePage(survey);
      handleSetCanSubmit(isLastPage);
    }
  }, [survey?.currentPageNo, checkIfLastVisiblePage, handleSetCanSubmit]);

  useEffect(
    () => () => {
      if (valueChangeTimeoutRef.current) {
        clearTimeout(valueChangeTimeoutRef.current);
      }
    },
    []
  );

  return (
    <>
      {shouldShowTitleAndDescription && (
        <div
          className={classNames('mb-6', {
            'px-6 pt-6': isRenderedInWebview,
            'pt-6': !isRenderedInWebview
          })}
          data-testid="survey-title-description"
        >
          {hasTitle && (
            <h2
              className={classNames('font-extrabold text-xl mb-3', {
                'text-gray-900': true
              })}
            >
              {schema?.title}
            </h2>
          )}
          {hasDescription && (
            <p
              className={classNames('font-bold text-base', {
                'text-gray-700': true,
                'mt-2': hasTitle
              })}
            >
              {schema?.description}
            </p>
          )}
        </div>
      )}
      <div
        className={classNames('font-normal text-sm text-gray-500', {
          'px-6 pt-6': isRenderedInWebview && !shouldShowTitleAndDescription,
          'px-6': isRenderedInWebview && shouldShowTitleAndDescription,
          'pt-6': !isRenderedInWebview && !shouldShowTitleAndDescription
        })}
      >
        {t('checkout:prePurchaseQuestionsDescription')}
      </div>
      <Survey model={survey} />
    </>
  );
}

export default forwardRef(Form);
